import { Controller } from '@hotwired/stimulus'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import {
  trans,
  ACTIVE,
  INACTIVE,
  ACCESS_REASON_LAST_ACTIVE_ERROR,
  ACCESS_REASON_UPDATE_FAILED
} from '../../translator'
declare const $: (el: HTMLElement) => { tooltip(action: string): void }

export default class AccessReasonsController extends Controller {
  static values = {
    createUrl: String
  }

  static targets = ['list', 'newReasonText']

  declare readonly createUrlValue: string
  declare readonly listTarget: HTMLElement
  declare readonly newReasonTextTarget: HTMLInputElement

  draggedItem: HTMLElement | null = null
  dropPlaceholder: HTMLElement | null = null

  connect (): void {
    window.addEventListener('pointerup', this.onPointerRelease)
    window.addEventListener('pointercancel', this.onPointerRelease)
    window.addEventListener('dragover', this.onWindowDragOver)
    this.listTarget.addEventListener('dragover', this.onListDragOver)
    this.listTarget.addEventListener('drop', this.onListDrop)

    this.listItems().forEach(item => {
      this.bindListItemEvents(item)
    })
  }

  disconnect (): void {
    window.removeEventListener('pointerup', this.onPointerRelease)
    window.removeEventListener('pointercancel', this.onPointerRelease)
    window.removeEventListener('dragover', this.onWindowDragOver)
    this.listTarget.removeEventListener('dragover', this.onListDragOver)
    this.listTarget.removeEventListener('drop', this.onListDrop)
    this.clearDropPlaceholder()

    this.listItems().forEach(item => {
      this.unbindListItemEvents(item)
    })
  }

  toggleActive = async (event: Event): Promise<void> => {
    const checkbox = event.currentTarget as HTMLInputElement

    // Ignore re-entrant toggles while a PATCH for this row is still in flight.
    if (checkbox.disabled) {
      return
    }

    const container = checkbox.closest<HTMLElement>('[data-update-url]')
    const url = container?.dataset.updateUrl ?? ''
    const isActive = checkbox.checked

    this.clearRowError(container)
    checkbox.disabled = true

    try {
      await fetchWithHandlingLoginRedirect(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: isActive })
      })
      this.updateReasonActiveState(container, isActive)
    } catch (error) {
      checkbox.checked = !isActive
      this.showRowError(container, error)
    } finally {
      checkbox.disabled = false
    }
  }

  async create (): Promise<void> {
    const text = this.newReasonTextTarget.value.trim()
    if (!text) return

    await fetchWithHandlingLoginRedirect(this.createUrlValue, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason_text: text })
    })
    window.location.reload()
  }

  async reorder (draggedEl: HTMLElement, targetEl: HTMLElement, insertBefore: boolean): Promise<void> {
    if (insertBefore) {
      this.listTarget.insertBefore(draggedEl, targetEl)
    } else {
      this.listTarget.insertBefore(draggedEl, targetEl.nextSibling)
    }

    await Promise.all(
      this.listItems().map((item, index) =>
        fetchWithHandlingLoginRedirect(item.dataset.updateUrl ?? '', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ position: index + 1 })
        })
      )
    )
  }

  private listItems (): HTMLElement[] {
    return Array.from(this.listTarget.querySelectorAll<HTMLElement>(':scope > [data-reason-id]'))
  }

  private ensureDropPlaceholder (): HTMLElement {
    if (this.dropPlaceholder !== null) {
      return this.dropPlaceholder
    }

    const placeholder = document.createElement('div')
    placeholder.dataset.dropPlaceholder = 'true'
    placeholder.style.backgroundColor = 'rgba(135, 192, 196, 0.12)'
    placeholder.style.border = '1px dashed #87C0C4'
    placeholder.style.borderRadius = '0.375rem'
    placeholder.style.margin = '0'
    placeholder.style.pointerEvents = 'none'
    placeholder.style.transition = 'height 120ms ease'

    this.dropPlaceholder = placeholder

    return placeholder
  }

  private clearDropPlaceholder (): void {
    this.dropPlaceholder?.remove()
    this.dropPlaceholder = null
  }

  private updateReasonActiveState (container: HTMLElement | null, isActive: boolean): void {
    if (container === null) {
      return
    }

    container.dataset.reasonActive = isActive ? 'true' : 'false'
    container.querySelector<HTMLElement>('[data-reason-text]')?.classList.toggle('text-muted', !isActive)

    const label = container.querySelector<HTMLElement>('label[data-toggle="tooltip"]')
    if (label !== null) {
      label.setAttribute('data-original-title', trans(isActive ? ACTIVE : INACTIVE, {}, 'states'))
      // Only hide any currently-visible tooltip so a stale one doesn't linger. The refreshed
      // title is read on the next hover; force-showing it here would leave a tooltip lingering
      // after the toggle, since a toggle is a click (not a hover) with no mouseleave to dismiss it.
      $(label).tooltip('hide')
    }
  }

  private clearRowError (container: HTMLElement | null): void {
    if (container === null) {
      return
    }

    const next = container.nextElementSibling

    if (next instanceof HTMLElement && next.dataset.reasonError === 'true') {
      next.remove()
    }
  }

  private showRowError (container: HTMLElement | null, error: unknown): void {
    if (container === null) {
      return
    }

    this.clearRowError(container)

    const message = document.createElement('div')
    message.dataset.reasonError = 'true'
    message.setAttribute('role', 'alert')
    message.className = 'text-danger small mt-1'
    message.textContent = this.errorMessageFor(error)

    container.insertAdjacentElement('afterend', message)
  }

  private errorMessageFor (error: unknown): string {
    if (typeof error === 'object' && error !== null && 'status' in error && (error as { status: unknown }).status === 400) {
      return trans(ACCESS_REASON_LAST_ACTIVE_ERROR, {}, 'associated-persons')
    }

    return trans(ACCESS_REASON_UPDATE_FAILED, {}, 'associated-persons')
  }

  private bindListItemEvents (item: HTMLElement): void {
    this.setItemDraggable(item, false)
    item.addEventListener('dragstart', this.onDragStart)
    item.addEventListener('dragover', this.onDragOver)
    item.addEventListener('drop', this.onDrop)
    item.addEventListener('dragend', this.onDragEnd)
    item.querySelector<HTMLInputElement>('input[type="checkbox"]')?.addEventListener('change', this.toggleActive)
    item.querySelector<HTMLElement>('[data-drag-handle]')?.addEventListener('pointerdown', this.onHandlePointerDown)
  }

  private unbindListItemEvents (item: HTMLElement): void {
    item.removeEventListener('dragstart', this.onDragStart)
    item.removeEventListener('dragover', this.onDragOver)
    item.removeEventListener('drop', this.onDrop)
    item.removeEventListener('dragend', this.onDragEnd)
    item.querySelector<HTMLInputElement>('input[type="checkbox"]')?.removeEventListener('change', this.toggleActive)
    item.querySelector<HTMLElement>('[data-drag-handle]')?.removeEventListener('pointerdown', this.onHandlePointerDown)
  }

  private cancelDragState (): void {
    this.draggedItem = null
    this.clearDropPlaceholder()
    this.resetDraggableItems()
  }

  private showDropPlaceholder (target: HTMLElement, insertBefore: boolean): void {
    if (this.draggedItem === null || target === this.draggedItem || !this.wouldChangePosition(target, insertBefore)) {
      this.clearDropPlaceholder()
      return
    }

    const placeholder = this.ensureDropPlaceholder()
    const draggedHeight = this.draggedItem.getBoundingClientRect().height
    const targetHeight = target.getBoundingClientRect().height

    placeholder.style.height = `${Math.max(draggedHeight, targetHeight)}px`

    if (insertBefore) {
      this.listTarget.insertBefore(placeholder, target)
      return
    }

    this.listTarget.insertBefore(placeholder, target.nextSibling)
  }

  private setItemDraggable (item: HTMLElement, isDraggable: boolean): void {
    item.setAttribute('draggable', isDraggable ? 'true' : 'false')
  }

  private shouldInsertBefore (target: HTMLElement, clientY: number): boolean {
    const rect = target.getBoundingClientRect()

    return clientY < rect.top + rect.height / 2
  }

  private isPointerWithinListBounds (event: DragEvent): boolean {
    const rect = this.listTarget.getBoundingClientRect()

    return event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
  }

  private resetDraggableItems (): void {
    this.listItems().forEach(item => {
      this.setItemDraggable(item, false)
    })
  }

  private onHandlePointerDown = (event: Event): void => {
    this.resetDraggableItems()

    const handle = event.currentTarget as HTMLElement
    const item = handle.closest<HTMLElement>('[data-reason-id]')

    if (item !== null) {
      this.setItemDraggable(item, true)
    }
  }

  private onDragStart = (event: DragEvent): void => {
    const item = event.currentTarget as HTMLElement

    if (item.getAttribute('draggable') !== 'true') {
      event.preventDefault()
      return
    }

    this.draggedItem = item
  }

  private onDragOver = (event: DragEvent): void => {
    event.preventDefault()

    const target = event.currentTarget as HTMLElement

    if (this.draggedItem === null || target === this.draggedItem) {
      this.clearDropPlaceholder()
      return
    }

    this.showDropPlaceholder(target, this.shouldInsertBefore(target, event.clientY))
  }

  private onDragEnd = (): void => {
    this.cancelDragState()
  }

  private onWindowDragOver = (event: DragEvent): void => {
    if (this.draggedItem === null) {
      return
    }

    const eventTarget = event.target

    if (eventTarget instanceof Node && this.listTarget.contains(eventTarget)) {
      return
    }

    if (!this.isPointerWithinListBounds(event)) {
      this.clearDropPlaceholder()
    }
  }

  private onListDragOver = (event: DragEvent): void => {
    if (this.draggedItem === null || event.target !== this.listTarget) {
      return
    }

    event.preventDefault()
  }

  private onPointerRelease = (): void => {
    if (this.draggedItem !== null) {
      return
    }

    this.resetDraggableItems()
  }

  private onDrop = (event: DragEvent): void => {
    event.preventDefault()
    const target = event.currentTarget as HTMLElement
    if (!this.draggedItem || target === this.draggedItem) return

    const insertBefore = this.shouldInsertBefore(target, event.clientY)
    this.clearDropPlaceholder()

    if (!this.wouldChangePosition(target, insertBefore)) {
      return
    }

    this.reorder(this.draggedItem, target, insertBefore).catch(() => undefined)
  }

  private onListDrop = (event: DragEvent): void => {
    if (this.draggedItem === null || this.dropPlaceholder === null || event.target !== this.listTarget) {
      return
    }

    event.preventDefault()

    const nextItem = this.dropPlaceholder.nextElementSibling as HTMLElement | null
    const previousItem = this.dropPlaceholder.previousElementSibling as HTMLElement | null

    this.clearDropPlaceholder()

    if (nextItem !== null && nextItem !== this.draggedItem) {
      this.reorder(this.draggedItem, nextItem, true).catch(() => undefined)
      return
    }

    if (previousItem !== null && previousItem !== this.draggedItem) {
      this.reorder(this.draggedItem, previousItem, false).catch(() => undefined)
    }
  }

  private wouldChangePosition (target: HTMLElement, insertBefore: boolean): boolean {
    if (this.draggedItem === null) {
      return false
    }

    const items = this.listItems()
    const draggedIndex = items.indexOf(this.draggedItem)
    const targetIndex = items.indexOf(target)

    if (draggedIndex === -1 || targetIndex === -1) {
      return false
    }

    let nextIndex = targetIndex

    if (insertBefore) {
      if (targetIndex > draggedIndex) {
        nextIndex -= 1
      }
    } else if (targetIndex <= draggedIndex) {
      nextIndex += 1
    }

    return nextIndex !== draggedIndex
  }
}
